import { hashKey, QueryKey, useQuery } from '@tanstack/react-query';
import { SourcingCampaignIndex } from 'api-clients/monolith';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { getCampaigns } from '@/api-clients/get-campaigns';
import { Pagination } from '@/components/base/pagination';
import { DataTable } from '@/components/ui/data-table';
import { useTableContext } from '@/contexts/table-context';
import { useFlag } from '@/flags';
import { useDataTable } from '@/hooks/use-data-table';
import { useLocalDateRanges } from '@/hooks/use-local-date-ranges';
import { usePostHog } from '@/hooks/use-posthog';
import { useReachBreakdowns } from '@/hooks/use-reach-breakdowns';
import { queryKeys } from '@/query-keys';
import { useWxCopilotSourceContext } from '@/hooks/use-wx-copilot-source-context';
import {
  sourceCampaignRowContext,
  sourceCampaignChannelContext,
} from '@/utils/source-copilot-context';

import {
  buildCampaignsFilterApplyProperties,
  type CampaignsEnumFilterKey,
} from '../filters/campaigns-filter-apply';
import { useCampaignFilters } from '../filters/use-campaign-filters';
import { useHiresToDate } from '../hires-to-date/context';
import { useRecountTint } from '../hires-to-date/recount-tint';
import { useCampaignsMetricsQueryOptions } from '../metrics/use-campaigns-metrics-query';
import { useCampaignColumns } from './columns';
import { NoCampaigns } from './no-campaigns';
import { CampaignsTimeZoneChip } from './time-zone-chip';
import {
  errorRowCells,
  emptyRowCells,
  loadingRowCells,
  otherRowCells,
  reachAdRowCells,
} from './reach-ad-rows';

const PINNED_LEFT_COLUMNS = [
  'menu',
  'toggle',
  'opening_name',
  'channel',
  'status',
];

// Assumes a `TableProvider` ancestor — the campaigns page (`campaigns/index.tsx`)
// owns a single shared provider so the search/filters/date-range bar above
// this table and the metrics cards above that both read/write the same state.
// The campaigns key ends with the Hires to date choice; a flip changes nothing else.
function isHiresToDateFlip(previousKey: QueryKey, nextKey: QueryKey) {
  return (
    hashKey(previousKey.slice(0, -1)) === hashKey(nextKey.slice(0, -1)) &&
    previousKey.at(-1) !== nextKey.at(-1)
  );
}

export function CampaignsTable() {
  const intl = useIntl();
  const posthog = usePostHog();
  const hasReachBreakdown = useFlag('source-reach-campaign-breakdown');
  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => new Set());
  const toggleBreakdown = (id: string) => {
    setExpandedIds(previous => {
      const next = new Set(previous);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  // Set when `searchQuery` commits to a non-empty value; consumed (reset to
  // null) the next time `data` settles, so a later pagination/sort refetch
  // with the same search term doesn't re-fire search_complete.
  const searchCommittedAtRef = useRef<number | null>(null);

  const {
    pagination: { pageIndex, pageSize },
    rowCount,
    setRowCount,
    searchQuery,
    filters: { channelIds, statuses, positionIds, locationIds, brandIds },
    dateRange,
    sorting,
    rowSelection,
  } = useTableContext();
  const sort = sorting[0];
  const localDays = useLocalDateRanges();
  const { isOn: hiresToDate } = useHiresToDate();

  const queryKey = [
    ...queryKeys.allCampaigns,
    pageIndex,
    pageSize,
    searchQuery,
    channelIds,
    statuses,
    positionIds,
    locationIds,
    brandIds,
    dateRange,
    sorting,
    hiresToDate,
  ];
  const { data, isLoading, isFetching, isError, isPlaceholderData } = useQuery({
    queryKey,
    // Flipping Hires to date keeps the rows on screen; only their hire columns
    // recount. Any other change loads the table afresh.
    placeholderData: (previous, previousQuery) =>
      previousQuery && isHiresToDateFlip(previousQuery.queryKey, queryKey)
        ? previous
        : undefined,
    queryFn: () =>
      getCampaigns({
        page: pageIndex + 1,
        perPage: pageSize,
        search: searchQuery,
        channelIds,
        statuses,
        positionIds,
        locationIds,
        brandIds,
        dateRange,
        localDays,
        // Omit both when there's no active sort so the backend applies its
        // own default (start_date DESC) rather than us forcing 'asc'.
        sortBy: sort?.id,
        sortDir: sort ? (sort.desc ? 'desc' : 'asc') : undefined,
        hiresToDate,
      }),
  });

  const tableRows = useMemo(() => data?.campaigns ?? [], [data]);
  const selectedIds = Object.keys(rowSelection).filter(id => rowSelection[id]);
  const selectedCampaign =
    selectedIds.length === 1
      ? tableRows.find(row => row.id === selectedIds[0])
      : undefined;
  const sourceCampaign = selectedCampaign
    ? sourceCampaignRowContext(selectedCampaign)
    : undefined;
  useWxCopilotSourceContext('campaigns', {
    sourceOpening: selectedCampaign
      ? {
          id: selectedCampaign.opening_external_id,
          name: selectedCampaign.opening_name,
        }
      : undefined,
    sourceCampaign,
    sourceChannel: sourceCampaign
      ? [sourceCampaignChannelContext(sourceCampaign)]
      : undefined,
  });
  const defaultMetricsPeriod = data?.default_metrics_period ?? null;
  const breakdowns = useReachBreakdowns(
    tableRows,
    dateRange,
    localDays,
    hasReachBreakdown,
    expandedIds,
  );
  const isTintingHires = useRecountTint(isPlaceholderData || isLoading);
  const columns = useCampaignColumns(
    tableRows,
    defaultMetricsPeriod,
    breakdowns,
    isPlaceholderData,
    isTintingHires,
    hasReachBreakdown ? { expandedIds, toggleBreakdown } : undefined,
  );

  // Sorting alone doesn't change which rows match — only search, facet
  // filters, and date range produce a "filtered empty" state.
  const isFiltered = Boolean(
    searchQuery?.trim() ||
    channelIds.length ||
    statuses.length ||
    positionIds.length ||
    locationIds.length ||
    brandIds.length ||
    dateRange.preset ||
    dateRange.startDate ||
    dateRange.endDate,
  );

  useEffect(() => {
    setRowCount(data?.total_count ?? 0);
  }, [data, setRowCount]);

  // Shares the metrics cards' cache entry, so it adds no request.
  const { data: metrics } = useQuery(useCampaignsMetricsQueryOptions());

  // One page view per landing, once the list and the metrics have both
  // loaded, so campaign_count is this page's total rather than a count left
  // over from an earlier query. Later searches and filters do not re-fire it.
  const hasTrackedPageViewRef = useRef(false);
  useEffect(() => {
    if (hasTrackedPageViewRef.current || !data || !metrics) return;

    hasTrackedPageViewRef.current = true;
    posthog.capture('source_campaigns:page_view', {
      campaign_count: data.total_count,
      any_filter_applied: isFiltered,
      is_zero_state: data.total_count === 0 && !isFiltered,
      total_spent: metrics.total_spend,
      average_cost_per_applicant: metrics.avg_cpa,
      total_applications: metrics.applicants,
      total_hires: metrics.hires,
      average_cost_per_hires: metrics.avg_cph,
    });
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [data, metrics]);

  const campaignFilters = useCampaignFilters();
  const optionsByKey = useMemo(
    () =>
      Object.fromEntries(
        campaignFilters.map(filter => [filter.filterKey, filter.options ?? []]),
      ) as Partial<
        Record<
          CampaignsEnumFilterKey,
          (typeof campaignFilters)[number]['options']
        >
      >,
    [campaignFilters],
  );

  // A filter change marks filter_apply pending; it fires once the list for
  // the new filters loads, so result_count is the filtered total. Changes made
  // before that load collapse into one event for the state that settles.
  const filterState = JSON.stringify([
    channelIds,
    statuses,
    positionIds,
    locationIds,
    brandIds,
    dateRange,
  ]);
  const lastFilterStateRef = useRef(filterState);
  const isFilterApplyPendingRef = useRef(false);
  useEffect(() => {
    if (filterState === lastFilterStateRef.current) return;

    lastFilterStateRef.current = filterState;
    isFilterApplyPendingRef.current = true;
  }, [filterState]);

  // A repeated filter or search state first shows its cached list while it
  // refetches, so both events wait for the fetch to settle, not the first data.
  useEffect(() => {
    if (isFetching || !data || !isFilterApplyPendingRef.current) return;

    isFilterApplyPendingRef.current = false;
    posthog.capture(
      'source_campaigns:filter_apply',
      buildCampaignsFilterApplyProperties({
        filters: { channelIds, statuses, positionIds, locationIds, brandIds },
        dateRange,
        resultCount: data.total_count,
        optionsByKey,
      }),
    );
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isFetching]);

  // A search loaded from the URL on landing is not one the recruiter ran, so
  // only a change after mount commits.
  const lastSearchQueryRef = useRef(searchQuery);
  useEffect(() => {
    if (searchQuery === lastSearchQueryRef.current) return;

    lastSearchQueryRef.current = searchQuery;
    searchCommittedAtRef.current = searchQuery?.trim() ? Date.now() : null;
  }, [searchQuery]);

  useEffect(() => {
    const committedAt = searchCommittedAtRef.current;
    if (isFetching || committedAt === null) return;

    const resultCount = data?.total_count ?? 0;
    posthog.capture('source_campaigns:search_complete', {
      result_count: resultCount,
      is_zero_results: resultCount === 0,
      duration_ms: Date.now() - committedAt,
    });
    searchCommittedAtRef.current = null;
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isFetching]);

  const { table } = useDataTable<SourcingCampaignIndex>({
    columns,
    data: tableRows,
    rowCount,
    externalIdKey: 'id',
    pinnedLeftColumns: PINNED_LEFT_COLUMNS,
  });

  if (isError) {
    throw new Error('Error fetching campaigns');
  }

  return (
    <>
      <DataTable
        table={table}
        columns={columns}
        isLoading={isLoading}
        emptyState={<NoCampaigns isFiltered={isFiltered} />}
        countLabel={intl.formatMessage({
          defaultMessage: 'campaigns',
          description: 'Count label: number of campaigns in the table',
        })}
        countBarEnd={localDays && <CampaignsTimeZoneChip />}
        getRowProps={row =>
          hasReachBreakdown && row.original.sourcing_channel.slug === 'reach'
            ? {
                className: 'cursor-pointer',
                onClick: event => {
                  const target = event.target as HTMLElement;
                  // Row actions and portaled menus keep their own behavior.
                  if (
                    !event.currentTarget.contains(target) ||
                    target.closest(
                      'button, a, input, select, textarea, [role="button"], [role="switch"], [role="menuitem"]',
                    )
                  )
                    return;
                  toggleBreakdown(row.original.id);
                },
              }
            : {}
        }
        renderExtraRows={
          hasReachBreakdown
            ? row => {
                const result = breakdowns.get(row.original.id);
                if (!result) return [];
                const key = `${row.original.id}-reach`;
                if (result.status === 'absent')
                  return [{ key, cells: emptyRowCells(true) }];
                if (result.status === 'loading')
                  return [{ key, cells: loadingRowCells() }];
                if (result.status === 'failed') {
                  return [{ key, cells: errorRowCells() }];
                }
                if (result.data?.state === 'unavailable')
                  return [{ key, cells: emptyRowCells(true) }];
                const data = result.data;
                if (!data) return [];
                if (data.ad_rows.length === 0 && !data.other_row)
                  return [{ key, cells: emptyRowCells() }];
                const rows = [...data.ad_rows]
                  .sort((a, b) => a.position - b.position)
                  .map(ad => ({
                    key: `${key}-${ad.position}`,
                    className: '[&>td]:bg-fx-neutral-100',
                    cells: reachAdRowCells(ad, intl),
                  }));
                if (data.other_row)
                  rows.push({
                    key: `${key}-other`,
                    className: '[&>td]:bg-fx-neutral-100',
                    cells: otherRowCells(data.other_row, intl),
                  });
                return rows;
              }
            : undefined
        }
      />
      {tableRows.length > 0 && <Pagination table={table} />}
    </>
  );
}
